import Button from "@/components/button"
import { Icon } from "@/icons/icon"
import { cn } from "@/lib/utils"
import { getItemSequenceLabel } from "@/packaging-v2/lib/utils"
import { V2ItemFormData } from "@/store/packaging-v2"
import { FormParamStatus } from "@/lib/form/types"

export interface ItemTabsProps {
  className?: string
  items: V2ItemFormData[]
  currentIndex: number
  disabled?: boolean
  disableAdd?: boolean
  onChange?: (index: number) => void
  onAdd?: () => void
}

/**
 * Horizontal scrollable tabs for switching between items
 */
export default function ItemTabs({
  className,
  items,
  currentIndex,
  disabled = false,
  disableAdd = false,
  onChange,
  onAdd,
}: ItemTabsProps) {
  function handleItemClick(index: number) {
    if (onChange && !disabled) {
      onChange(index)
    }
  }

  function handleAddClick() {
    if (onAdd && !disabled) {
      onAdd()
    }
  }

  function getItemStatus(item: V2ItemFormData): "new" | "draft" | "submitted" {
    if (item.status === FormParamStatus.SUBMITTED) {
      return "submitted"
    }
    if (item.status === FormParamStatus.DRAFT) {
      return "draft"
    }
    if (item.productId && item.skuId) {
      return "draft"
    }
    return "new"
  }

  function getStatusIndicator(status: "new" | "draft" | "submitted") {
    switch (status) {
      case "submitted":
        return (
          <span className="absolute -top-1 -right-1 size-3 bg-green-500 rounded-full border-2 border-white" />
        )
      case "draft":
        return (
          <span className="absolute -top-1 -right-1 size-3 bg-yellow-500 rounded-full border-2 border-white" />
        )
      default:
        return null
    }
  }

  return (
    <div className={cn("flex items-center gap-2 overflow-x-auto py-2", className)}>
      {items.map((item, index) => {
        const isActive = index === currentIndex
        const status = getItemStatus(item)

        return (
          <button
            key={item.id}
            type="button"
            className={cn(
              "relative flex-shrink-0 px-4 py-2 rounded-lg border text-sm font-medium transition-colors",
              isActive
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={disabled}
            onClick={() => handleItemClick(index)}
          >
            {getItemSequenceLabel(item.sequenceOrder)}
            {getStatusIndicator(status)}
          </button>
        )
      })}

      {/* Add Item Button */}
      {!disabled && !disableAdd && (
        <Button
          variant="outline"
          className="flex-shrink-0 px-3 py-2"
          onClick={handleAddClick}
        >
          <Icon name="Plus" className="size-4" />
        </Button>
      )}
    </div>
  )
}

/**
 * Empty state when no items exist
 */
export function ItemTabsEmpty({
  className,
  onAdd,
}: {
  className?: string
  onAdd?: () => void
}) {
  return (
    <div className={cn("flex items-center gap-4 py-2", className)}>
      <span className="text-gray-500 text-sm">No items yet</span>
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Icon name="Plus" className="size-4 mr-1" />
        Add First Item
      </Button>
    </div>
  )
}
